if (!customElements.get("page-navigator")) {
  class ComponentPageNavigator extends HTMLElement {
    constructor() {
      super();
      this.onLinkClick = this.onLinkClick.bind(this);
      this.onFixedLinkClick = this.onFixedLinkClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.requestUpdate = this.requestUpdate.bind(this);
      this.isConnectedComponent = false;
      this.frameId = null;
      this.validTargets = [];
      this.targetObserver = null;
      this.hadTabindex = this.hasAttribute('tabindex');
    }

    connectedCallback() {
      if (this.isConnectedComponent) return;

      this.isConnectedComponent = true;
      this.navigationItemsEl = this.querySelector('.mobile-nav .navigation-items');
      this.itemsPerViewTablet = this.dataset.itemsPerViewTablet ? parseFloat(this.dataset.itemsPerViewTablet) : 1;
      this.itemsPerViewMobile = this.dataset.itemsPerViewMobile ? parseFloat(this.dataset.itemsPerViewMobile) : 1;
      this.itemsSpacing = this.dataset.itemsSpacing ? parseFloat(this.dataset.itemsSpacing) : 10;
      const configuredOffset = Number.parseFloat(this.dataset.scrollOffset);
      this.scrollOffset = Number.isFinite(configuredOffset) ? configuredOffset : 0;
      const configuredActiveSectionOffset = Number.parseFloat(this.dataset.activeSectionOffset);
      this.activeSectionOffset = Number.isFinite(configuredActiveSectionOffset) ? configuredActiveSectionOffset : 100;
      this.desktopLayout = this.dataset.layoutDesktop || 'default';
      this.mobileLayout = this.dataset.layoutMobile || 'carousel';
      this.linksEl = this.querySelectorAll('[data-link]');
      this.toggleEl = this.querySelector('.page-navigator-toggle');
      this.disclosureEl = this.querySelector('[data-page-navigator-disclosure]');
      this.dropdownEl = this.querySelector('.page-navigator-dropdown');
      this.titleEl = this.querySelector('[data-page-navigator-title]');
      this.counterEl = this.querySelector('[data-page-navigator-counter]');
      this.fallbackTitle = this.titleEl?.textContent.trim() || '';
      this.isDesktopLayout = Boolean(window.$s?.media?.isLarge);

      this.initLinks();
      this.initSlider();
      this.initFixedNavigation();
    }

    disconnectedCallback() {
      if (!this.isConnectedComponent) return;

      this.isConnectedComponent = false;
      this.linksEl.forEach((linkEl) => linkEl.removeEventListener('click', this.onLinkClick));
      this.fixedLinksEl?.forEach((linkEl) => linkEl.removeEventListener('click', this.onFixedLinkClick));
      document.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('scroll', this.requestUpdate);
      window.removeEventListener('resize', this.requestUpdate);
      window.removeEventListener('mediaChange', this.onViewportChange);
      window.removeEventListener('keydown', this.onKeydown);
      if (this.frameId) window.cancelAnimationFrame(this.frameId);
      this.frameId = null;
      if (this.targetObserver) this.targetObserver.disconnect();
      this.targetObserver = null;
      if (this.carousel) this.carousel.destroy();
      this.carousel = null;
      this.querySelectorAll('.page-navigator-fixed').forEach((navEl) => {
        navEl.classList.remove('is-enhanced', 'is-visible');
        navEl.hidden = false;
      });
      if (this.dropdownEl) this.dropdownEl.hidden = false;
      if (this.disclosureEl) this.disclosureEl.open = false;
      if (this.counterEl) this.counterEl.hidden = true;
      this.clearActiveTarget();
      this.resetMobileSummary();
      this.validTargets = [];
      if (!this.hadTabindex) this.removeAttribute('tabindex');
    }

    initLinks() {
      this.linksEl.forEach((linkEl) => linkEl.addEventListener('click', this.onLinkClick));
    }

    onLinkClick(event) {
      const linkEl = event.currentTarget;
      if (!linkEl.dataset.link) return;
      const targetEl = document.getElementById(linkEl.dataset.link);
      const utilities = window.$s;
      const fixedNavEl = linkEl.closest('.page-navigator-fixed');
      if (!targetEl || !utilities || (fixedNavEl && !fixedNavEl.classList.contains('is-enhanced'))) return;

      event.preventDefault();
      utilities.scrollToElement(targetEl, this.scrollOffset);
      this.setActiveTarget(linkEl.dataset.link);
      this.closeDropdown();
    }

    initSlider() {
      if (this.mobileLayout !== 'carousel' || !this.navigationItemsEl || !window.KeenSlider) return;

      const breakpoints = {};
      breakpoints['(max-width: ' + window.breakpoints.small + ')'] = {
        slidesPerView: this.itemsPerViewMobile
      };
      this.carousel = new KeenSlider(this.navigationItemsEl, {
        mode: "snap",
        loop: true,
        wheel: false,
        slidesPerView: this.itemsPerViewTablet,
        spacing: this.itemsSpacing,
        centered: false,
        breakpoints
      });
    }

    initFixedNavigation() {
      if ((this.desktopLayout !== 'fixed' && this.mobileLayout !== 'fixed') || !window.$s) return;

      this.refreshFixedTargets();
      this.fixedLinksEl = this.querySelectorAll('.mobile-nav.page-navigator-fixed [data-fixed-navigation-item] a');
      this.fixedLinksEl.forEach((linkEl) => linkEl.addEventListener('click', this.onFixedLinkClick));
      window.addEventListener('scroll', this.requestUpdate, { passive: true });
      window.addEventListener('resize', this.requestUpdate);
      window.addEventListener('mediaChange', this.onViewportChange);
      window.addEventListener('keydown', this.onKeydown);
      document.addEventListener('pointerdown', this.onPointerDown);
      this.observeTargets();
      this.requestUpdate();
    }

    getValidTargets() {
      const targetIds = [];
      this.querySelectorAll('.page-navigator-fixed').forEach((navEl) => {
        const renderedTargetIds = [];
        navEl.querySelectorAll('[data-fixed-navigation-item]').forEach((itemEl) => {
          const linkEl = itemEl.querySelector('[data-link]');
          const targetId = linkEl?.dataset.link;
          const isInternal = Boolean(targetId);
          const isValid = !isInternal || Boolean(document.getElementById(targetId));
          const isDuplicate = isInternal && isValid && renderedTargetIds.includes(targetId);
          itemEl.hidden = !isValid || isDuplicate;
          if (isInternal && isValid && !isDuplicate) renderedTargetIds.push(targetId);
          if (isInternal && isValid && !targetIds.includes(targetId)) targetIds.push(targetId);
        });
      });

      return targetIds;
    }

    refreshFixedTargets() {
      this.validTargets = this.getValidTargets();
      const hasUsableItems = this.getVisibleFixedItems().length > 0;
      this.reconcileFixedFocus(hasUsableItems);
      if (hasUsableItems) {
        this.querySelectorAll('.page-navigator-fixed').forEach((navEl) => {
          navEl.hidden = false;
          navEl.classList.add('is-enhanced');
        });
        if (!this.validTargets.length) {
          this.clearActiveTarget();
          this.setNeutralMobileSummary();
        }
        return true;
      }

      this.clearActiveTarget();
      if (this.querySelector('.mobile-nav.page-navigator-fixed')?.contains(document.activeElement)) this.focusDesktopEquivalent(document.activeElement);
      this.closeDropdown(false, false);
      this.querySelectorAll('.page-navigator-fixed').forEach((navEl) => {
        navEl.classList.remove('is-visible', 'is-enhanced');
        navEl.hidden = true;
      });
      this.resetMobileSummary();
      return false;
    }

    observeTargets() {
      if (!window.MutationObserver || !document.body) return;

      const targetIds = Array.from(this.querySelectorAll('[data-fixed-navigation-item] [data-link]')).map((linkEl) => linkEl.dataset.link);
      this.targetObserver = new MutationObserver((mutations) => {
        const hasRelevantChange = mutations.some((mutation) => {
          if (mutation.type === 'attributes') return targetIds.includes(mutation.target.id) || targetIds.includes(mutation.oldValue);
          return Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes)).some((node) => this.nodeContainsTargetId(node, targetIds));
        });
        if (!hasRelevantChange) return;

        this.refreshFixedTargets();
        this.requestUpdate();
      });
      this.targetObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['id'], attributeOldValue: true });
    }

    nodeContainsTargetId(node, targetIds) {
      if (node.nodeType !== Node.ELEMENT_NODE) return false;
      if (targetIds.includes(node.id)) return true;
      return Array.from(node.querySelectorAll('[id]')).some((element) => targetIds.includes(element.id));
    }

    onPointerDown(event) {
      if (this.disclosureEl?.open && !this.disclosureEl.contains(event.target)) this.closeDropdown();
    }

    onFixedLinkClick(event) {
      this.closeDropdown(false, false);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.disclosureEl?.open) {
        this.closeDropdown(true);
      }
    }

    onViewportChange() {
      const isDesktop = Boolean(window.$s?.media?.isLarge);
      const sourceEl = document.activeElement;
      const mobileNavEl = this.querySelector('.mobile-nav');
      const desktopNavEl = this.querySelector('.desktop-nav');
      const moveToDesktop = !this.isDesktopLayout && isDesktop && mobileNavEl?.contains(sourceEl);
      const moveToMobile = this.isDesktopLayout && !isDesktop && desktopNavEl?.contains(sourceEl);
      this.isDesktopLayout = isDesktop;
      this.closeDropdown(false, false);
      this.updateFixedNavigation();
      if (moveToDesktop) this.focusDesktopEquivalent(sourceEl);
      if (moveToMobile) this.focusMobileEquivalent(sourceEl);
    }

    requestUpdate() {
      if (this.frameId) return;
      this.frameId = window.requestAnimationFrame(() => {
        this.frameId = null;
        this.updateFixedNavigation();
      });
    }

    updateFixedNavigation() {
      const isDesktop = Boolean(window.$s?.media?.isLarge);
      const activeLayout = isDesktop ? this.desktopLayout : this.mobileLayout;
      const fixedNavEl = this.querySelector(isDesktop ? '.desktop-nav.page-navigator-fixed' : '.mobile-nav.page-navigator-fixed');
      const topPosition = parseFloat(getComputedStyle(this).getPropertyValue(isDesktop ? '--page-navigator-top-desktop' : '--page-navigator-top-mobile')) || 0;

      this.querySelectorAll('.page-navigator-fixed').forEach((navEl) => {
        const isVisible = navEl === fixedNavEl && activeLayout === 'fixed' && this.getVisibleFixedItems().length > 0 && this.getBoundingClientRect().top <= topPosition;
        if (!isVisible && navEl.contains(document.activeElement)) {
          this.closeDropdown(false, false);
          this.focusHost();
        }
        navEl.classList.toggle('is-visible', isVisible);
      });

      if (activeLayout !== 'fixed' || !this.validTargets.length) return;
      const activeTargetOffset = topPosition + (this.activeSectionOffset || 1);
      const activeTargetId = this.validTargets.reduce((currentTargetId, targetId) => {
        const targetEl = document.getElementById(targetId);
        return targetEl && targetEl.getBoundingClientRect().top <= activeTargetOffset ? targetId : currentTargetId;
      }, null);
      if (activeTargetId) {
        this.setActiveTarget(activeTargetId);
      } else {
        this.clearActiveTarget();
        this.setNeutralMobileSummary();
      }
    }

    setActiveTarget(targetId) {
      const currentIndex = this.validTargets.indexOf(targetId);
      if (currentIndex === -1) return;

      this.querySelectorAll('[data-fixed-navigation-item]').forEach((itemEl) => {
        const linkEl = itemEl.querySelector('[data-link]');
        const isActive = linkEl?.dataset.link === targetId;
        itemEl.classList.toggle('active', isActive);
        if (linkEl) {
          if (isActive) {
            linkEl.setAttribute('aria-current', 'location');
          } else {
            linkEl.removeAttribute('aria-current');
          }
        }
      });

      const mobileItems = Array.from(this.querySelectorAll('.mobile-nav [data-fixed-navigation-item]')).filter((itemEl) => !itemEl.hidden);
      const activeItemIndex = mobileItems.findIndex((itemEl) => itemEl.querySelector('[data-link]')?.dataset.link === targetId);
      const activeLinkEl = mobileItems[activeItemIndex]?.querySelector('[data-link]');
      if (this.titleEl && activeLinkEl) this.titleEl.textContent = activeLinkEl.textContent.trim();
      if (this.counterEl && activeItemIndex !== -1) {
        this.counterEl.hidden = false;
        this.counterEl.textContent = String(activeItemIndex + 1).padStart(2, '0') + ' / ' + String(mobileItems.length).padStart(2, '0');
      }
    }

    clearActiveTarget() {
      this.querySelectorAll('[data-fixed-navigation-item]').forEach((itemEl) => {
        itemEl.classList.remove('active');
        itemEl.querySelector('[data-link]')?.removeAttribute('aria-current');
      });
    }

    getVisibleMobileItems() {
      return Array.from(this.querySelectorAll('.mobile-nav [data-fixed-navigation-item]')).filter((itemEl) => !itemEl.hidden);
    }

    getVisibleFixedItems() {
      return Array.from(this.querySelectorAll('.page-navigator-fixed [data-fixed-navigation-item]')).filter((itemEl) => !itemEl.hidden);
    }

    reconcileFixedFocus(hasUsableItems) {
      const activeElement = document.activeElement;
      const hiddenItemEl = activeElement?.closest('[data-fixed-navigation-item][hidden]');
      if (!hiddenItemEl) return;

      this.closeDropdown(false, false);
      const isDesktop = Boolean(window.$s?.media?.isLarge);
      const mobileNavEl = this.querySelector('.mobile-nav.page-navigator-fixed');
      if (hasUsableItems && !isDesktop && this.disclosureEl && !mobileNavEl?.hidden) {
        this.toggleEl?.focus();
        return;
      }
      this.focusDesktopEquivalent(activeElement);
    }

    focusDesktopEquivalent(sourceEl) {
      const sourceLinkEl = sourceEl?.closest('a');
      const desktopLinks = Array.from(this.querySelectorAll('.desktop-nav a')).filter((linkEl) => !linkEl.closest('[hidden]') && linkEl.offsetParent !== null && getComputedStyle(linkEl).visibility !== 'hidden');
      const matchingLinkEl = sourceLinkEl && desktopLinks.find((linkEl) => linkEl.dataset.link === sourceLinkEl.dataset.link || linkEl.getAttribute('href') === sourceLinkEl.getAttribute('href'));
      if (matchingLinkEl) {
        matchingLinkEl.focus();
        return;
      }
      if (desktopLinks[0]) {
        desktopLinks[0].focus();
        return;
      }
      this.setAttribute('tabindex', '-1');
      this.focus();
    }

    focusMobileEquivalent(sourceEl) {
      const sourceLinkEl = sourceEl?.closest('a');
      const mobileNavEl = this.querySelector('.mobile-nav');
      const mobileLinks = Array.from(this.querySelectorAll('.mobile-nav a')).filter((linkEl) => !linkEl.closest('[hidden]') && linkEl.offsetParent !== null && getComputedStyle(linkEl).visibility !== 'hidden');
      const matchingLinkEl = sourceLinkEl && mobileLinks.find((linkEl) => linkEl.dataset.link === sourceLinkEl.dataset.link || linkEl.getAttribute('href') === sourceLinkEl.getAttribute('href'));
      if (matchingLinkEl) {
        matchingLinkEl.focus();
        return;
      }
      if (mobileLinks[0]) {
        mobileLinks[0].focus();
        return;
      }
      if (this.toggleEl && mobileNavEl && !mobileNavEl.hidden && this.toggleEl.offsetParent !== null && getComputedStyle(this.toggleEl).visibility !== 'hidden') {
        this.toggleEl.focus();
        return;
      }
      this.setAttribute('tabindex', '-1');
      this.focus();
    }

    focusHost() {
      this.setAttribute('tabindex', '-1');
      this.focus({ preventScroll: true });
    }

    setNeutralMobileSummary() {
      const mobileItems = this.getVisibleMobileItems();
      const fallbackLinkEl = mobileItems[0]?.querySelector('a');
      if (this.titleEl) this.titleEl.textContent = fallbackLinkEl?.textContent.trim() || this.fallbackTitle;
      if (this.counterEl) {
        this.counterEl.hidden = mobileItems.length === 0;
        this.counterEl.textContent = mobileItems.length ? '01 / ' + String(mobileItems.length).padStart(2, '0') : '';
      }
    }

    resetMobileSummary() {
      if (this.titleEl) this.titleEl.textContent = this.fallbackTitle;
      if (this.counterEl) {
        this.counterEl.hidden = true;
        this.counterEl.textContent = '';
      }
    }

    closeDropdown(restoreFocus = false, restoreContainedFocus = true) {
      if (!this.dropdownEl || !this.disclosureEl || !this.disclosureEl.open) return;

      const shouldRestoreFocus = restoreFocus || (restoreContainedFocus && this.dropdownEl.contains(document.activeElement));
      this.disclosureEl.open = false;
      if (shouldRestoreFocus) this.toggleEl?.focus();
    }
  }
  customElements.define("page-navigator", ComponentPageNavigator);
}
